<script setup lang="ts">
  /**
   * Login View
   *
   * Authentication page for all domains.
   * Redirects to appropriate dashboard after login based on role and domain.
   */
  import { ref, computed } from 'vue'
  import { useAuth } from '../../composables/useAuth'
  import { getAppDomain, type AppDomain } from '../../utils/domain'
  import logoHorizontal from '../../assets/logos/claimshield-horizontal.png'
  import logoHorizontalReverse from '../../assets/logos/claimshield-horizontal-reverse.png'

  const currentDomain: AppDomain = getAppDomain()

  const { login, isLoading } = useAuth()

  const email = ref('')
  const password = ref('')
  const rememberMe = ref(false)
  const error = ref('')

  const domainLabel = computed(() => {
    switch (currentDomain) {
      case 'portal':
        return 'Client Portal'
      case 'ops':
        return 'Operations'
      default:
        return 'Operations'
    }
  })

  async function handleSubmit() {
    error.value = ''

    if (!email.value || !password.value) {
      error.value = 'Please enter your email and password'
      return
    }

    const success = await login({
      username: email.value,
      password: password.value,
    })

    if (!success) {
      error.value = 'Invalid email or password'
    }
  }

</script>

<template>
  <div class="login-page flex min-h-screen bg-white">
    <!-- Full-page container -->
    <div class="flex w-full">

      <!-- Left panel — gradient mesh with rounded inset -->
      <div class="mesh-panel relative m-3 hidden w-7/12 rounded-3xl lg:flex lg:flex-col lg:justify-between lg:px-10 lg:pb-10 lg:pt-8">
        <div class="mesh-bg rounded-3xl" />

        <!-- Top logo -->
        <div class="relative z-10 pt-2">
          <img
            :src="logoHorizontalReverse"
            alt="ClaimShield"
            class="h-9 w-auto"
          />
        </div>

        <!-- Bottom text — domain-aware -->
        <div class="relative z-10">
          <template v-if="currentDomain === 'ops'">
            <p class="text-sm font-medium uppercase tracking-wider text-white/40">Internal Ops</p>
            <p class="mt-2 text-2xl font-semibold leading-tight text-white">
              Where compliance<br />gets done.
            </p>
          </template>
          <template v-else-if="currentDomain === 'portal'">
            <p class="text-sm font-medium uppercase tracking-wider text-white/40">Client Portal</p>
            <p class="mt-2 text-2xl font-semibold leading-tight text-white">
              Your fleet. Your data.<br />Always current.
            </p>
          </template>
          <template v-else>
            <p class="text-sm font-medium uppercase tracking-wider text-white/40">Internal Ops</p>
            <p class="mt-2 text-2xl font-semibold leading-tight text-white">
              Where compliance<br />gets done.
            </p>
          </template>
        </div>
      </div>

      <!-- Right panel — form -->
      <div class="flex w-full flex-col justify-center px-8 py-10 lg:w-5/12 lg:px-14 lg:py-14">
        <!-- Mobile logo -->
        <div class="mb-6 lg:hidden">
          <img
            :src="logoHorizontal"
            alt="ClaimShield"
            class="h-8 w-auto"
          />
        </div>

        <!-- Header -->
        <div class="mb-6">
          <span class="text-xs font-medium uppercase tracking-widest text-primary-500">
            {{ domainLabel }}
          </span>
          <h1 class="mt-1 text-xl font-bold text-secondary-900">
            Welcome back
          </h1>
          <p class="mt-1 text-sm text-secondary-400">
            Sign in to access your dashboard.
          </p>
        </div>

        <!-- Error -->
        <div
          v-if="error"
          class="mb-5 flex items-center gap-2 rounded-lg bg-danger-50 px-4 py-3 text-sm text-danger-700"
        >
          <i class="pi pi-exclamation-circle text-danger-500" />
          <span>{{ error }}</span>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div>
            <label for="email" class="mb-1.5 block text-sm font-medium text-secondary-700">
              Your email
            </label>
            <PInputText
              id="email"
              v-model="email"
              type="email"
              placeholder="you@company.com"
              class="light-input w-full"
              :disabled="isLoading"
              autocomplete="email"
            />
          </div>

          <div>
            <label for="password" class="mb-1.5 block text-sm font-medium text-secondary-700">
              Password
            </label>
            <PPassword
              id="password"
              v-model="password"
              :feedback="false"
              toggleMask
              placeholder="Enter your password"
              class="light-input w-full"
              inputClass="w-full"
              :disabled="isLoading"
              autocomplete="current-password"
            />
          </div>

          <div class="flex items-center justify-between">
            <label class="flex cursor-pointer items-center gap-2.5 text-sm text-secondary-500">
              <span class="light-checkbox" :class="{ checked: rememberMe }">
                <input
                  type="checkbox"
                  v-model="rememberMe"
                  class="sr-only"
                />
                <i v-if="rememberMe" class="pi pi-check" />
              </span>
              Remember me
            </label>
            <RouterLink
              to="/forgot-password"
              class="text-sm text-secondary-400 transition-colors hover:text-primary-600"
            >
              Forgot password?
            </RouterLink>
          </div>

          <PButton
            type="submit"
            label="Sign in"
            :loading="isLoading"
            class="primary-btn w-full"
          />
        </form>

        <!-- Footer -->
        <p class="mt-8 text-center text-xs text-secondary-300">
          &copy; {{ new Date().getFullYear() }} ClaimShield &middot; Safety &amp; Compliance Services
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Page ── */
.login-page {
  background: #fff;
  min-height: 100vh;
}

/* ── Left mesh gradient panel ── */
.mesh-panel {
  position: relative;
  overflow: hidden;
  border-radius: 1.5rem;
}

.mesh-bg {
  position: absolute;
  inset: 0;
  background: #3d2683;
  /* Layered radial gradients for mesh effect */
  background:
    radial-gradient(ellipse at 20% 50%, rgba(107, 79, 175, 0.9) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.7) 0%, transparent 50%),
    radial-gradient(ellipse at 40% 80%, rgba(59, 130, 246, 0.5) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 70%, rgba(168, 85, 247, 0.6) 0%, transparent 45%),
    radial-gradient(ellipse at 10% 20%, rgba(236, 72, 153, 0.3) 0%, transparent 50%),
    linear-gradient(135deg, #2a1a5e, #6b4faf);
  filter: blur(0px);
}

/* Soft blur overlay on the mesh for smoothness */
.mesh-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(40px);
}

/* ── Animated mesh — visible color shift ── */
.mesh-bg {
  background-size: 200% 200% !important;
  animation: mesh-shift 6s ease-in-out infinite;
}

@keyframes mesh-shift {
  0%, 100% { background-position: 0% 50%; }
  25% { background-position: 100% 25%; }
  50% { background-position: 50% 100%; }
  75% { background-position: 25% 0%; }
}


/* ── Light inputs ── */
input.light-input,
.light-input :deep(input) {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 0.5rem !important;
  padding: 0.5rem 0.75rem !important;
  font-size: 0.875rem !important;
  color: #1e293b !important;
  transition: border-color 0.2s, box-shadow 0.2s !important;
}

input.light-input:focus,
.light-input :deep(input:focus) {
  border-color: #6b4faf !important;
  box-shadow: 0 0 0 3px rgba(107, 79, 175, 0.1) !important;
  outline: none !important;
}

input.light-input::placeholder,
.light-input :deep(input::placeholder) {
  color: #94a3b8 !important;
}

.light-input :deep(.p-password-toggle-mask-btn) {
  color: #94a3b8;
}

.light-input :deep(.p-password-toggle-mask-btn:hover) {
  color: #64748b;
}

/* ── Checkbox ── */
.light-checkbox {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border: 1.5px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #fff;
  transition: all 0.15s;
  flex-shrink: 0;
}

.light-checkbox.checked {
  background: #3d2683;
  border-color: #3d2683;
}

.light-checkbox .pi-check {
  font-size: 0.625rem;
  color: #fff;
  font-weight: 700;
}

/* ── Primary button — matches left panel gradient ── */
.primary-btn {
  background: linear-gradient(135deg, #6b4faf, #3d2683) !important;
  border: none !important;
  color: #fff !important;
  border-radius: 0.5rem !important;
  padding: 0.55rem 1.25rem !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  transition: all 0.2s !important;
}

.primary-btn:hover {
  background: linear-gradient(135deg, #7c63bf, #4a30a0) !important;
  box-shadow: 0 4px 12px rgba(107, 79, 175, 0.3) !important;
}

.primary-btn:active {
  background: linear-gradient(135deg, #5a3f9e, #2a1a5e) !important;
}
</style>
